import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toastMessage } from '@/utils/toast-message';
import {
  LayoutDashboard, Users, BookOpen, MessageSquare,
  Bell, Wallet, GraduationCap, Calendar,
  CheckCircle2, Loader2, ChevronRight,
} from 'lucide-react';
import { useParentAppSchool } from '../ParentAppContext';
import { apiRequest } from '@/utils/api-request';

interface AcademicYear {
  id: number;
  year_label: string;
  start_date: string;
  end_date: string;
}

function greeting() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12)  return 'Good morning';
  if (h >= 12 && h < 17) return 'Good afternoon';
  return 'Good evening';
}

const TODAY = new Date().toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

const QUICK_ACTIONS = [
  { path: 'teachers',      label: 'Teachers',       desc: 'Manage staff',          icon: Users,          cat: 'cat-blue'   },
  { path: 'students',      label: 'Students',        desc: 'Enrolment & profiles',  icon: GraduationCap,  cat: 'cat-green'  },
  { path: 'curriculum',    label: 'Academic Setup',  desc: 'Classes & subjects',    icon: BookOpen,       cat: 'cat-purple' },
  { path: 'class-feed',    label: 'Class Feed',      desc: 'Monitor posts',         icon: MessageSquare,  cat: 'cat-teal'   },
  { path: 'notifications', label: 'Communication Hub',   desc: 'Push to parents',       icon: Bell,           cat: 'cat-amber'  },
  { path: 'fees',          label: 'Fees',            desc: 'Payments & dues',       icon: Wallet,         cat: 'cat-peach'  },
];

export default function ParentAppDashboard() {
  const navigate = useNavigate();
  const { school, refreshStatus } = useParentAppSchool();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [loading,        setLoading]        = useState(true);
  const [academicYears,  setAcademicYears]  = useState<AcademicYear[]>([]);
  const [showYearMenu,   setShowYearMenu]   = useState(false);
  const [activatingYear, setActivatingYear] = useState<number | null>(null);
  const [stats, setStats] = useState({ totalStudents: 0, totalClasses: 0, totalTeachers: 0 });

  useEffect(() => {
    fetchStats();
    fetchAcademicYears();
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
        setShowYearMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const res = await apiRequest('/api/parent-app/academic-years', { method: 'GET' });
      if (res.success) setAcademicYears(res.data?.years || []);
    } catch {}
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await apiRequest('/api/parent-app/admin/school/stats', { method: 'GET' });
      if (res.success) setStats({
        totalStudents: res.data.totalStudents || 0,
        totalClasses:  res.data.totalClasses  || 0,
        totalTeachers: res.data.totalTeachers || 0,
      });
    } catch {}
    finally { setLoading(false); }
  };

  const handleActivateYear = async (yearId: number) => {
    if (activatingYear) return;
    try {
      setActivatingYear(yearId);
      const res = await apiRequest(`/api/parent-app/activate-academic-year/${yearId}`, { method: 'POST' });
      if (res.success) {
        await Promise.all([refreshStatus(), fetchAcademicYears()]);
        setShowYearMenu(false);
        toastMessage(`Switched to ${academicYears.find(y => y.id === yearId)?.year_label ?? 'selected year'}`, 'success');
      } else {
        toastMessage('Failed to switch year', 'error');
      }
    } catch { toastMessage('Failed to switch year', 'error'); }
    finally { setActivatingYear(null); }
  };

  const activeYear = academicYears.find(y => y.id === school?.activeAcademicYearId);

  /* Attention chips — derived from setup state */
  const chips: { label: string; path: string }[] = [];
  if (school?.setup) {
    if (!school.setup.hasTeachers) chips.push({ label: 'Add your first teacher', path: 'teachers' });
    if (!school.setup.hasClasses)  chips.push({ label: 'Set up classes', path: 'curriculum' });
    if (!school.setup.hasStudents) chips.push({ label: 'Invite students', path: 'students' });
  }

  return (
    <>
      {/* Page header */}
      <div className="page-head">
        <div className="page-head-icon">
          <LayoutDashboard size={20} strokeWidth={2} />
        </div>
        <div className="page-head-text">
          <h1>Dashboard</h1>
          <p>{school?.school_name ? `${school.school_name} · Portal overview` : 'Portal overview'}</p>
        </div>
        <div className="page-head-actions">
          {/* Academic year selector */}
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <button
              className="btn btn-soft"
              onClick={() => setShowYearMenu(v => !v)}
            >
              {activatingYear
                ? <Loader2 size={14} style={{ animation: 'spin .7s linear infinite' }} />
                : <Calendar size={14} />}
              Academic Year {activeYear?.year_label || '—'}
              <ChevronRight size={13} style={{ transform: showYearMenu ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform .15s' }} />
            </button>

            {showYearMenu && (
              <div className="card" style={{
                position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                width: 240, padding: 6, zIndex: 50, boxShadow: 'var(--sh-lg)',
              }}>
                <p className="meta" style={{ padding: '6px 10px 8px', fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  Academic Year
                </p>
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {academicYears.map(year => {
                    const isActive = school?.activeAcademicYearId === year.id;
                    return (
                      <button
                        key={year.id}
                        onClick={() => handleActivateYear(year.id)}
                        disabled={!!activatingYear}
                        className="nav-item"
                        style={{
                          borderRadius: 9, padding: '9px 10px', width: '100%',
                          background: isActive ? 'var(--accent-wash)' : 'transparent',
                          borderLeft: isActive ? '3px solid var(--accent-deep)' : '3px solid transparent',
                          opacity: activatingYear ? 0.6 : 1,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text)' }}>{year.year_label}</div>
                          <div className="meta" style={{ marginTop: 1 }}>
                            {new Date(year.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            {' – '}
                            {new Date(year.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        </div>
                        {activatingYear === year.id
                          ? <Loader2 size={14} style={{ animation: 'spin .7s linear infinite', flexShrink: 0, color: 'var(--accent-deep)' }} />
                          : isActive
                            ? <CheckCircle2 size={15} style={{ flexShrink: 0, color: 'var(--success)' }} />
                            : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        {/* ── Hero card ── */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24, background: 'var(--slate)' }}>
          <div style={{ position: 'relative', padding: '26px 28px', color: '#fff', overflow: 'hidden' }}>
            {/* Amber glow orb */}
            <div style={{
              position: 'absolute', right: -40, top: -60,
              width: 260, height: 260, borderRadius: '50%',
              background: 'radial-gradient(circle, var(--accent) 0%, transparent 62%)',
              opacity: 0.18, pointerEvents: 'none',
            }} />
            <div style={{ position: 'relative' }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--accent)' }}>
                {greeting()}
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.6px', marginTop: 7, lineHeight: 1.2 }}>
                {chips.length > 0
                  ? `${chips.length} thing${chips.length > 1 ? 's' : ''} need your attention`
                  : `Welcome back${school?.school_name ? `, ${school.school_name}` : ''}`}
              </div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.6)', fontWeight: 500, marginTop: 5 }}>
                {TODAY}
              </div>
              {chips.length > 0 && (
                <div className="row gap-8" style={{ marginTop: 16, flexWrap: 'wrap' }}>
                  {chips.map(chip => (
                    <button
                      key={chip.path}
                      onClick={() => navigate(`/parent-app/${chip.path}`)}
                      className="chip"
                      style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.15)' }}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Stat cards ── */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="stat" style={{ opacity: 0.4 }}>
                <div className="stat-icon" style={{ background: 'var(--slate-soft)', width: 38, height: 38 }} />
                <div style={{ height: 24, width: 50, background: 'var(--bg-2)', borderRadius: 6, marginTop: 14 }} />
                <div style={{ height: 11, width: 80, background: 'var(--bg-2)', borderRadius: 4, marginTop: 7 }} />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
            <button className="stat" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => navigate('/parent-app/students')}>
              <div className="stat-icon cat-green"><Users size={18} strokeWidth={2} /></div>
              <div className="stat-num">{stats.totalStudents}</div>
              <div className="stat-label">Total students</div>
            </button>
            <button className="stat" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => navigate('/parent-app/teachers')}>
              <div className="stat-icon cat-blue"><GraduationCap size={18} strokeWidth={2} /></div>
              <div className="stat-num">{stats.totalTeachers}</div>
              <div className="stat-label">Teaching staff</div>
            </button>
            <button className="stat" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => navigate('/parent-app/curriculum')}>
              <div className="stat-icon cat-purple"><BookOpen size={18} strokeWidth={2} /></div>
              <div className="stat-num">{stats.totalClasses}</div>
              <div className="stat-label">Active classes</div>
            </button>
            <div className="stat">
              <div className="stat-icon cat-amber"><Calendar size={18} strokeWidth={2} /></div>
              <div className="stat-num" style={{ fontSize: 20 }}>{activeYear?.year_label || '—'}</div>
              <div className="stat-label">Academic year</div>
            </div>
          </div>
        )}

        {/* ── Quick actions ── */}
        <div style={{ marginBottom: 8 }}>
          <p className="meta" style={{ fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 14 }}>
            Quick actions
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {QUICK_ACTIONS.map(a => {
              const Icon = a.icon;
              return (
                <button
                  key={a.path}
                  className="card"
                  style={{ padding: 18, textAlign: 'left', cursor: 'pointer', border: '1px solid var(--border)', transition: 'box-shadow .15s, border-color .15s' }}
                  onClick={() => navigate(`/parent-app/${a.path}`)}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'var(--sh-md)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-2)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'var(--sh-sm)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; }}
                >
                  <div className={`stat-icon ${a.cat}`} style={{ marginBottom: 14 }}>
                    <Icon size={18} strokeWidth={2} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.2px', color: 'var(--text)' }}>{a.label}</div>
                  <div className="meta" style={{ marginTop: 3 }}>{a.desc}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
